import React, { useEffect, useState } from 'react';
import styles from './index.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faHome, faClipboard, faPhone, faLock, faGraduationCap, faCreditCard, faBuildingColumns, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { faApple, faGooglePay, faCcVisa, faCcMastercard, faCcAmex, faPaypal, faWhatsapp } from '@fortawesome/free-brands-svg-icons';

const HowToPurchase = () => {
    const [expandedStep, setExpandedStep] = useState(null);
    const [expandedFaq, setExpandedFaq] = useState(null);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const toggleStep = (stepNumber) => {
        setExpandedStep(expandedStep === stepNumber ? null : stepNumber);
    };

    const toggleFaq = (faqIndex) => {
        setExpandedFaq(expandedFaq === faqIndex ? null : faqIndex);
    };

    // Helper function to convert URLs in text to clickable links
    const renderTextWithLinks = (text) => {
        if (!text) return text;

        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const parts = text.split(urlRegex);

        return parts.map((part, index) => {
            if (urlRegex.test(part)) {
                return (
                    <a
                        key={index}
                        href={part}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color: 'var(--secondary)',
                            textDecoration: 'underline',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'opacity 0.3s ease',
                        }}
                        onMouseEnter={(e) => (e.target.style.opacity = '0.8')}
                        onMouseLeave={(e) => (e.target.style.opacity = '1')}
                    >
                        {part}
                    </a>
                );
            }
            return part;
        });
    };

    const registrationSteps = [
        {
            number: 1,
            title: 'Go to Studyhub Website',
            description: 'Studyhub වෙත පිවිසීමට https://www.studyhub.lk/ වෙත යන්න.',
            details: 'ඔබගේ web browser විවෘත කර Studyhub ප්‍රධාන වෙබ් අඩවියට පිවිසෙන්න. එහිදී ඔබට ඉගෙනුම් අවස්ථා ඇතුළත් Homepage දැකගත හැක.',
            icon: faHome,
        },
        {
            number: 2,
            title: 'Click the Register Button',
            description: 'Navigation Bar හි ඇති Register button click කරන්න.',
            details: 'මෙය මුල් පිටුවේ ඉහළ දකුණු කෙළවරෙහි පිහිටා ඇත. Register form පිවිසීම සඳහා "Register" button මත click කරන්න.',
            icon: faClipboard,
        },
        {
            number: 3,
            title: 'Access the Registration Form',
            description: `ඔබ https://www.studyhub.lk/auth/register වෙත යොමු කරනු ලැබේ.`,
            details: 'එහිදී ඔබගේ තොරතුරු ඇතුළත් කිරීමට අවශ්‍ය form එක දිස්වනු ඇත. අවශ්‍ය සියලුම තොරතුරු නිවැරදිව පුරවන්න.',
            icon: faGraduationCap,
        },
        {
            number: 4,
            title: 'Complete Registration Form',
            description: 'ඔබගේ තොරතුරු ඇතුළත් කර "Create Account" button click කරන්න.',
            details: 'Enter your Full Name, Email Address, Phone Number, Password, and Confirm Password. Check the box to agree to Privacy Policy and Terms & Conditions, then click "Create Account".',
            icon: faPhone,
        },
    ];

    const paymentSteps = [
        {
            number: 5,
            title: 'View Featured Courses',
            description: 'ලියාපදිංචි වීමෙන් පසු Home page හි ඇති Featured Courses යටතේ Generative AI for Beginners ඔබට දැකගත හැක.',
            details: 'Click "වැඩි විස්තර බැලීම සඳහා "Enroll Now" click කරන්න. එසේ නැතිනම් https://www.studyhub.lk/courses/generative-ai-for-beginners වෙත පිවිසෙන්න.',
            icon: faGraduationCap,
        },
        {
            number: 6,
            title: 'Course Details',
            description: 'Course details page displays pricing, instructor details, duration, course overview, course content, and objectives. Click Purchase button to proceed with payment. Click the "Purchase" button in the course page to purchase the course.',
            details: 'පාඨමාලාවේ මිල, උපදේශක තොරතුරු, කාලසීමාව, පාඨමාලාව පිළිබඳ හැඳින්වීම, අන්තර්ගතය සහ අරමුණු මෙහි දැකගත හැක. ගෙවීම් ක්‍රියාවලියට යාම සඳහා "Purchase" මත click කරන්න.',
            icon: faCircleInfo,
        },
        {
            number: 7,
            title: 'Choose Payment Method',
            description: 'ඔබට පහසු ගෙවීම් ක්‍රමයක් තෝරන්න - Online Payment (Debit/Credit Card), Bank Transfer හෝ WhatsApp.',
            details: 'The payment page will show three options. ඔබට වඩාත් පහසු ක්‍රමය තෝරා ගන්න.',
            methods: [
                { name: 'Online Payment', icon: faCreditCard, description: 'Debit හෝ Credit කාඩ්පත් භාවිතයෙන්' },
                { name: 'Bank Transfer', icon: faBuildingColumns, description: 'බැංකු තැන්පතුවක් මඟින්' },
                { name: 'WhatsApp', icon: faBuildingColumns, description: 'ගෙවීම් රිසිට්පත WhatsApp හරහා යොමු කිරීම' },
            ],
            icon: faCreditCard,
        },
        {
            number: 8,
            title: 'Online Payment Process',
            description: 'Debit හෝ Credit Card භාවිතයෙන් ගෙවීම් කිරීම සඳහා "Checkout" බොත්තම මත ක්ලික් කරන්න.',
            details: 'ඔබ PayHere payment gateway වෙත යොමු කරනු ලැබේ. එහිදී Visa, Mastercard හෝ American Express කාඩ්පත් තොරතුරු ඇතුළත් කිරීමෙන් හෝ ඔබගේ බැංකුව තෝරා ගෙවීම සිදු කළ හැක.',
            icon: faLock,
        },
        {
            number: 9,
            title: 'Bank Transfer Method',
            description: 'Bank Transfer/Deposit විකල්පය තෝරා ලබා දී ඇති බැංකු ගිණුමට මුදල් තැන්පත් කරන්න.',
            details: 'Account details payment form දැක්වෙනු ඇත. පාඨමාලා ගාස්තුව ගෙවා රිසිට්පත Upload කරන්න. Payment receipt upload කිරීමෙන් පසු "Submit" click කරන්න.',
            bankDetails: {
                accountName: 'Ardillabs (PVT) LTD',
                accountNumber: '12512168892',
                bankName: 'Commercial Bank of Ceylon',
                branch: 'Dhanpal',
            },
            icon: faBuildingColumns,
        },
        {
            number: 10,
            title: 'WhatsApp Payment Option',
            description: 'ඔබට ඔබගේ ගෙවීම් රිසිට්පත WhatsApp හරහාද යොමු කළ හැක.',
            details: 'WhatsApp විකල්පය තෝරා ලබා දී ඇති WhatsApp අංකයට ඔබගේ ගෙවීම් රිසිට්පත යොමු කරන්න. අපගේ කණ්ඩායම ඔබගේ ගෙවීම තහවුරු කිරීමෙන් පසු පාඨමාලාවට ප්‍රවේශය ලබා දෙනු ඇත.',
            icon: faWhatsapp,
        },
    ];

    const faqs = [
        {
            question: 'Do I need to create an account to purchase a course?',
            answer: 'Yes, you must create a Studyhub account before purchasing any course. This allows us to track your progress and provide you with certificates upon completion.',
        },
        {
            question: 'What payment methods are available?',
            answer: 'We accept online payments via Debit/Credit Cards (Visa, Mastercard, American Express), Bank Transfers/Deposits, and WhatsApp payments. You can choose the method most convenient for you.',
        },
        {
            question: 'Is my payment information secure?',
            answer: 'Yes, all online payments are processed through PayHere, a certified and secure payment gateway that uses industry-standard encryption to protect your financial information.',
        },
        {
            question: 'How long does it take to get access after payment?',
            answer: 'For online payments, you get instant access to the course. For bank transfers and WhatsApp payments, access is granted within 1-2 business hours after we verify your payment receipt.',
        },
        {
            question: 'Can I get a refund?',
            answer: 'Please refer to our Payment Policy for our refund terms and conditions. Most courses offer a limited refund period if you haven\'t started the course content.',
        },
        {
            question: 'Can I purchase multiple courses at once?',
            answer: 'Yes, you can purchase multiple courses. Each course must be registered and paid for separately through the course page.',
        },
        {
            question: 'What if I encounter an error during payment?',
            answer: 'If you face any payment issues, please contact our support team through the Contact Us page or email us directly. We\'ll be happy to help resolve the issue.',
        },
        {
            question: 'Do I get a certificate after completing a course?',
            answer: 'Yes! Upon successful completion of the course requirements, you will receive a completion certificate that you can download and share.',
        },
    ];

    const openLogin = () => {
        window.open(
            "https://www.studyhub.lk/courses/generative-ai-for-beginners",
            "_blank",
            "noopener,noreferrer"
        );
    };

    return (
        <div className={styles.howToPurchaseContainer}>
            <div className="main-container">
                <div className="container-content">
                    {/* Header Section */}
                    <div className={styles.headerSection}>
                        <h1 className={styles.mainTitle}>How to Purchase a Course</h1>
                        <p className={styles.subtitle}>
                            Follow our simple step-by-step guide to register and start learning at Studyhub
                        </p>
                    </div>

                    {/* Registration Section */}
                    <div className={styles.processSection}>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Step 1: Registration Process</h2>
                            <p className={styles.sectionDescription}>
                                Create your Studyhub account to get started
                            </p>
                        </div>

                        <div className={styles.stepsContainer}>
                            {registrationSteps.map((step) => (
                                <div
                                    key={step.number}
                                    className={`${styles.stepCard} ${expandedStep === step.number ? styles.expanded : ''
                                        }`}
                                    onClick={() => toggleStep(step.number)}
                                >
                                    <div className={styles.stepHeader}>
                                        <div className={styles.stepNumberIcon}>
                                            <FontAwesomeIcon
                                                icon={step.icon}
                                                className={styles.stepIcon}
                                            />
                                        </div>
                                        <div className={styles.stepTitleContainer}>
                                            <h3 className={styles.stepTitle}>{step.title}</h3>
                                            <p className={styles.stepDescription}>{renderTextWithLinks(step.description)}</p>
                                        </div>
                                        <div className={styles.chevronIcon}>
                                            <FontAwesomeIcon
                                                icon={expandedStep === step.number ? faChevronUp : faChevronDown}
                                            />
                                        </div>
                                    </div>
                                    {expandedStep === step.number && (
                                        <div className={styles.stepDetails}>
                                            <p className={styles.detailsText}>{renderTextWithLinks(step.details)}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Payment Section */}
                    <div className={styles.processSection}>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Step 2: Payment & Course Access</h2>
                            <p className={styles.sectionDescription}>
                                Choose your preferred payment method and complete your purchase
                            </p>
                        </div>

                        <div className={styles.stepsContainer}>
                            {paymentSteps.map((step) => (
                                <div
                                    key={step.number}
                                    className={`${styles.stepCard} ${expandedStep === step.number ? styles.expanded : ''
                                        }`}
                                    onClick={() => toggleStep(step.number)}
                                >
                                    <div className={styles.stepHeader}>
                                        <div className={styles.stepNumberIcon}>
                                            <FontAwesomeIcon
                                                icon={step.icon}
                                                className={styles.stepIcon}
                                            />
                                        </div>
                                        <div className={styles.stepTitleContainer}>
                                            <h3 className={styles.stepTitle}>{step.title}</h3>
                                            <p className={styles.stepDescription}>{renderTextWithLinks(step.description)}</p>
                                        </div>
                                        <div className={styles.chevronIcon}>
                                            <FontAwesomeIcon
                                                icon={expandedStep === step.number ? faChevronUp : faChevronDown}
                                            />
                                        </div>
                                    </div>
                                    {expandedStep === step.number && (
                                        <div className={styles.stepDetails}>
                                            <p className={styles.detailsText}>{renderTextWithLinks(step.details)}</p>
                                            {step.methods && (
                                                <div className={styles.methodsGrid}>
                                                    {step.methods.map((method, idx) => (
                                                        <div key={idx} className={styles.methodCard}>
                                                            <FontAwesomeIcon
                                                                icon={method.icon}
                                                                className={styles.methodIcon}
                                                            />
                                                            <h4 className={styles.methodName}>{method.name}</h4>
                                                            <p className={styles.methodDesc}>{method.description}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {step.bankDetails && (
                                                <div className={styles.bankDetails}>
                                                    <h4 className={styles.bankDetailsTitle}>Bank Account Details:</h4>
                                                    <div className={styles.bankInfo}>
                                                        <p>
                                                            <strong>Account Name:</strong> {step.bankDetails.accountName}
                                                        </p>
                                                        <p>
                                                            <strong>Account No:</strong> {step.bankDetails.accountNumber}
                                                        </p>
                                                        <p>
                                                            <strong>Bank:</strong> {step.bankDetails.bankName}
                                                        </p>
                                                        <p>
                                                            <strong>Branch:</strong> {step.bankDetails.branch}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Payment Methods Section */}
                    <div className={styles.paymentMethodsSection}>
                        <h2 className={styles.sectionTitle}>Supported Payment Methods</h2>
                        <div className={styles.paymentMethodsGrid}>
                            <div className={styles.paymentMethodCard}>
                                <FontAwesomeIcon
                                    icon={faCreditCard}
                                    className={styles.largeIcon}
                                />
                                <h3>Credit/Debit Cards</h3>
                                <div className={styles.cardBrands}>
                                    <FontAwesomeIcon icon={faCcVisa} />
                                    <FontAwesomeIcon icon={faCcMastercard} />
                                    <FontAwesomeIcon icon={faCcAmex} />
                                </div>
                            </div>
                            <div className={styles.paymentMethodCard}>
                                <FontAwesomeIcon
                                    icon={faBuildingColumns}
                                    className={styles.largeIcon}
                                />
                                <h3>Bank Transfer</h3>
                                <p>Direct bank deposit to our account</p>
                            </div>
                            <div className={styles.paymentMethodCard}>
                                <FontAwesomeIcon
                                    icon={faWhatsapp}
                                    className={styles.largeIcon}
                                />
                                <h3>WhatsApp</h3>
                                <p>Send receipt via WhatsApp</p>
                            </div>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <div className={styles.faqSection}>
                        <div className={styles.faqHeader}>
                            <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
                            <p className={styles.sectionDescription}>
                                Find answers to common questions about purchasing courses
                            </p>
                        </div>

                        <div className={styles.faqContainer}>
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className={`${styles.faqItem} ${expandedFaq === index ? styles.faqExpanded : ''
                                        }`}
                                    onClick={() => toggleFaq(index)}
                                >
                                    <div className={styles.faqQuestion}>
                                        <h4>{faq.question}</h4>
                                        <FontAwesomeIcon
                                            icon={expandedFaq === index ? faChevronUp : faChevronDown}
                                            className={styles.faqChevron}
                                        />
                                    </div>
                                    {expandedFaq === index && (
                                        <div className={styles.faqAnswer}>
                                            <p>{faq.answer}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* CTA Section */}
                    <section className={styles.ctaSection}>
                        <h3>Start your AI journey today</h3>
                        <p>Understand how AI works and build intelligent applications yourself.</p>
                        <button
                            className={styles.ctaButton} type="button"
                            onClick={openLogin}
                        >
                            Enroll Now
                        </button>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default HowToPurchase;
